import { ref, computed, type Ref, type ComputedRef } from 'vue';
import { z } from 'zod';

type FormValidationReturn<T> = {
    form: Ref<T>;
    errors: Ref<Partial<Record<keyof T, string[]>>>;
    validate: () => boolean;
};

type SchemaOrGetter<T extends z.ZodType> = T | (() => T);

export const useFormValidation = <T extends z.ZodType>(
    schemaOrGetter: SchemaOrGetter<T>,
    initialValues: z.infer<T>
): FormValidationReturn<z.infer<T>> => {
    const form = ref<z.infer<T>>(initialValues) as Ref<z.infer<T>>;
    const errors = ref<Partial<Record<keyof z.infer<T>, string[]>>>({}) as Ref<
        Partial<Record<keyof z.infer<T>, string[]>>
    >;

    const schema: ComputedRef<T> = computed(() =>
        typeof schemaOrGetter === 'function' ? (schemaOrGetter as () => T)() : schemaOrGetter
    );

    const validate = (): boolean => {
        const result = schema.value.safeParse(form.value);
        if (!result.success) {
            errors.value = z.flattenError(result.error).fieldErrors;
            return false;
        }
        errors.value = {};
        return true;
    };

    return { form, errors, validate };
};
